import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { isAdminRole, signOutToHome } from "../../lib/auth";
import { useAuth } from "../../lib/authContext";
import { pickAvatarFile, uploadAvatar } from "../../lib/profileUploads";
import { supabase } from "../../lib/supabase";
import { useMarketingFonts } from "../../lib/useMarketingFonts";

const BLACK = COLORS.BLACK.hex;
const FOREST = COLORS.FOREST.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const FOREST_DEEP = "#1A2618";
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = COLORS.RED_ERROR.hex;

const MAX_BYTES = 5 * 1024 * 1024;

export default function EmployeeOnboardScreen() {
  const fontsReady = useMarketingFonts();
  const { user, role, employeeStatus, loading: authLoading, refetchProfile } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [pickedFile, setPickedFile] = useState<Awaited<ReturnType<typeof pickAvatarFile>>>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Routing guards (preserved from the legacy file unchanged): bounce
  // unauthed users to /login, non-admins back to home, and active /
  // pending → wherever they actually belong. Only `pending` lingers
  // here long enough to render the form.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.replace("/login" as Href);
      return;
    }
    if (!isAdminRole(role)) {
      router.replace("/" as Href);
      return;
    }
    if (employeeStatus === "rejected") {
      return;
    }
    if (employeeStatus === "active") {
      router.replace("/admin" as Href);
      return;
    }
    if (employeeStatus !== "pending" && employeeStatus != null) {
      router.replace("/admin" as Href);
      return;
    }
  }, [authLoading, user, role, employeeStatus]);

  async function pickPhoto() {
    setErr(null);
    const file = await pickAvatarFile();
    if (!file) return;
    const size = (file as { fileSize?: number }).fileSize;
    if (typeof size === "number" && size > MAX_BYTES) {
      setErr("Photo must be under 5MB.");
      return;
    }
    setPickedFile(file);
    setPreviewUri(file.uri);
  }

  async function saveAndContinue(skip: boolean) {
    setErr(null);
    if (!skip) {
      if (!firstName.trim() || !lastName.trim()) {
        setErr("First and last name are required.");
        return;
      }
    }
    setBusy(true);
    try {
      if (!skip && pickedFile) {
        await uploadAvatar(pickedFile);
      }
      if (!skip) {
        const full = `${firstName.trim()} ${lastName.trim()}`.trim();
        const { error } = await supabase.from("user_profiles").update({ full_name: full }).eq("id", user!.id);
        if (error) throw new Error(error.message);
      }
      const { error: metaErr } = await supabase.auth.updateUser({
        data: { employee_onboard_completed: true },
      });
      if (metaErr) throw new Error(metaErr.message);
      await refetchProfile();
      router.replace("/admin/mfa/enroll" as Href);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (!fontsReady || authLoading) {
    return (
      <View style={{ flex: 1, backgroundColor: BLACK, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator color={AMBER} />
      </View>
    );
  }

  if (employeeStatus === "rejected") {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BLACK }} edges={["top", "left", "right"]}>
        <View style={{ flex: 1, justifyContent: "center", paddingHorizontal: 24 }}>
          <View
            style={{
              alignSelf: "center",
              maxWidth: 480,
              padding: 28,
              backgroundColor: FOREST_DEEP,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 8,
            }}
          >
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 9.5,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                color: RED,
              }}
            >
              Access revoked
            </Text>
            <Text style={{ marginTop: 10, fontFamily: "CormorantGaramond_500Medium", fontSize: 26, color: LINEN }}>
              Your access was revoked.
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: "DMSans_400Regular",
                fontSize: 13.5,
                lineHeight: 21,
                color: WS,
              }}
            >
              Contact Coravu if you believe this is a mistake. Otherwise sign out and we'll see
              you down the line.
            </Text>
            <Pressable
              onPress={() => void signOutToHome(router)}
              style={[
                {
                  marginTop: 18,
                  alignSelf: "flex-start",
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 4,
                  backgroundColor: AMBER,
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 11.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                Sign out
              </Text>
            </Pressable>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  const fullName = `${firstName} ${lastName}`.trim();
  const initials = makeInitials(fullName) ?? "?";

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BLACK }} edges={["top", "left", "right"]}>
      <ScrollView contentContainerStyle={{ paddingHorizontal: 22, paddingVertical: 32, alignItems: "center" }}>
        <View style={{ width: "100%", maxWidth: 540 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 9.5,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              First-time setup
            </Text>
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: "CormorantGaramond_400Regular",
              fontSize: 36,
              lineHeight: 40,
              color: LINEN,
            }}
          >
            Complete your profile.
          </Text>
          <Text
            style={{
              marginTop: 6,
              fontFamily: "DMSans_300Light",
              fontSize: 14,
              lineHeight: 22,
              color: WM,
            }}
          >
            Tell us a bit about yourself to finish setting up your account. You can change any of
            this later from Account & security.
          </Text>

          <View
            style={{
              marginTop: 24,
              padding: 22,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              backgroundColor: FOREST_DEEP,
            }}
          >
            <FieldLabel>Profile photo</FieldLabel>
            <View style={{ marginTop: 10, flexDirection: "row", alignItems: "center", gap: 14 }}>
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.32)",
                  backgroundColor: "rgba(181,135,58,0.16)",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {previewUri ? (
                  <Image source={{ uri: previewUri }} style={{ width: 64, height: 64 }} />
                ) : (
                  <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 22, color: AMBER }}>
                    {initials}
                  </Text>
                )}
              </View>
              <View style={{ flex: 1 }}>
                <Pressable
                  onPress={() => void pickPhoto()}
                  style={[
                    {
                      alignSelf: "flex-start",
                      paddingHorizontal: 14,
                      paddingVertical: 8,
                      borderRadius: 4,
                      borderWidth: 1,
                      borderColor: BORDER,
                      backgroundColor: "rgba(255,255,255,0.02)",
                    },
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: "DMSans_700Bold",
                      fontSize: 11,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: WS,
                    }}
                  >
                    {previewUri ? "Change photo" : "Choose photo"}
                  </Text>
                </Pressable>
                <Text style={{ marginTop: 6, fontFamily: "DMSans_400Regular", fontSize: 11.5, color: WL }}>
                  Optional. JPG or PNG, max 5MB.
                </Text>
              </View>
            </View>

            <View style={{ marginTop: 18, flexDirection: "row", gap: 12 }}>
              <View style={{ flex: 1 }}>
                <FieldLabel required>First name</FieldLabel>
                <DarkInput
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="Jane"
                  autoCapitalize="words"
                />
              </View>
              <View style={{ flex: 1 }}>
                <FieldLabel required>Last name</FieldLabel>
                <DarkInput
                  value={lastName}
                  onChange={setLastName}
                  placeholder="Smith"
                  autoCapitalize="words"
                />
              </View>
            </View>

            {err ? (
              <Text style={{ marginTop: 12, fontFamily: "DMSans_500Medium", fontSize: 12.5, color: RED }}>
                {err}
              </Text>
            ) : null}

            <View style={{ marginTop: 22, flexDirection: "row", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <Pressable
                onPress={() => void saveAndContinue(false)}
                disabled={busy}
                style={[
                  {
                    paddingHorizontal: 22,
                    paddingVertical: 12,
                    borderRadius: 4,
                    backgroundColor: AMBER,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    opacity: busy ? 0.5 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                {busy ? <ActivityIndicator size="small" color={FOREST} /> : null}
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 11.5,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: FOREST,
                  }}
                >
                  {busy ? "Saving…" : "Continue →"}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => void saveAndContinue(true)}
                disabled={busy}
                style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
              >
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: WM,
                  }}
                >
                  Skip for now
                </Text>
              </Pressable>
            </View>
          </View>

          <Text
            style={{
              marginTop: 18,
              fontFamily: "DMSans_400Regular",
              fontSize: 11.5,
              color: WL,
              textAlign: "center",
            }}
          >
            Next up: enable multi-factor authentication. Coravu admins are required to use MFA.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function FieldLabel({ children, required }: { children: string; required?: boolean }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
      <Text
        style={{
          fontFamily: "DMSans_700Bold",
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {children}
      </Text>
      {required ? <Text style={{ color: RED, fontSize: 10 }}>*</Text> : null}
    </View>
  );
}

function DarkInput({
  value,
  onChange,
  placeholder,
  autoCapitalize,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      placeholderTextColor={WL}
      autoCapitalize={autoCapitalize}
      style={
        {
          marginTop: 6,
          backgroundColor: FOREST_MID,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 4,
          paddingHorizontal: 12,
          paddingVertical: 10,
          color: W,
          fontFamily: "DMSans_400Regular",
          fontSize: 13,
          ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
        } as unknown as TextStyle
      }
    />
  );
}

function makeInitials(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  const parts = t.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
